import type { ContractTypePlanDraft } from "@/components/assistant/contractTypeGuidelinesDraft";
import type { WriteApprovalEntityKind } from "@/components/assistant/formatWriteApprovalSummary";
import type { ContractTypeGuidelineDocumentRecord } from "@/types";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type PendingGuidelinePdf = {
  key: string;
  file: File | null;
  fileName: string;
  extractedText: string;
  summarized: boolean;
  /** Saved guideline PDF this summary replaces. Removing the summary restores it. */
  replacesSavedId?: string;
};

export type EditablePlanControls = {
  draft: Record<string, string>;
  onDraftChange: (draft: Record<string, string>) => void;
  assistantProposalConflict: boolean;
  onApplyAssistantProposal: () => void;
  onKeepLocalEdits: () => void;
  instructionSourceText?: string;
  onInstructionSummarized?: (summary: string) => void;
  referenceDocuments?: Array<{ id: string; fileName: string; extractedText: string }>;
};

export type ContractTypePlanControls = {
  draft: ContractTypePlanDraft;
  onDraftChange: (draft: ContractTypePlanDraft) => void;
  pendingPdfs: PendingGuidelinePdf[];
  onQueuePdf: (payload: { file: File; extractedText: string }) => void;
  onRemovePendingPdf: (key: string) => void;
  savedDocs: ContractTypeGuidelineDocumentRecord[];
  getAccessToken: () => Promise<string | null>;
  assistantProposalConflict: boolean;
  onApplyAssistantProposal: () => void;
  onKeepLocalEdits: () => void;
  onFoldReferencePdfs: () => void;
};

export type AssistantPlanSnapshot = {
  kind: WriteApprovalEntityKind;
  mode: "create" | "update";
  proposal: Record<string, unknown>;
  approvalReady: boolean;
  disabled: boolean;
  submitDisabled?: boolean;
  onCreate: () => void;
  planTitle: string;
  contractTypePlan?: ContractTypePlanControls;
  editablePlan?: EditablePlanControls;
};

type AssistantPlanContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  snapshot: AssistantPlanSnapshot | null;
  setSnapshot: (snapshot: AssistantPlanSnapshot | null) => void;
};

const AssistantPlanContext = createContext<AssistantPlanContextValue | null>(null);

export function AssistantPlanProvider({ children }: { children: ReactNode }) {
  const [open, setOpenState] = useState(false);
  const [snapshot, setSnapshotState] = useState<AssistantPlanSnapshot | null>(null);

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next);
  }, []);

  const setSnapshot = useCallback((next: AssistantPlanSnapshot | null) => {
    setSnapshotState(next);
  }, []);

  const value = useMemo(
    () => ({ open, setOpen, snapshot, setSnapshot }),
    [open, setOpen, snapshot, setSnapshot]
  );

  return (
    <AssistantPlanContext.Provider value={value}>{children}</AssistantPlanContext.Provider>
  );
}

export function useAssistantPlan() {
  const ctx = useContext(AssistantPlanContext);
  if (!ctx) {
    throw new Error("useAssistantPlan must be used within AssistantPlanProvider");
  }
  return ctx;
}

export function useAssistantPlanOptional() {
  return useContext(AssistantPlanContext);
}
